import { useState } from 'react';
import { XIcon, UsersIcon } from './Icons';

const inputCls = "w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13.5px] text-slate-700 bg-white outline-none focus:border-[#fb866d] transition-all placeholder:text-slate-400";
const labelCls = "block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5";
const sectionCls = "sm:col-span-2 text-[11px] font-bold text-[#e8614a] uppercase tracking-widest pt-3 pb-1 border-t border-slate-100 mt-2";

export default function EmployeeModal({ initial, projects, sponsorships = [], saving, error, onSave, onClose, isEdit }) {
  const [form, setForm] = useState({ ...initial });
  const set = e => setForm(p => {
    const next = { ...p, [e.target.name]: e.target.value };
    if (e.target.name === 'basicSalary' || e.target.name === 'allowance') {
      next.totalSalary = (Number(next.basicSalary) || 0) + (Number(next.allowance) || 0);
    }
    return next;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-fit max-w-full max-h-[90vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
            <UsersIcon size={18} className="text-white" />
          </div>
          <h2 className="text-base font-bold text-slate-800 flex-1">{isEdit ? 'Edit Employee' : 'Add New Employee'}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Full Name *</label>
              <input className={inputCls} name="name" placeholder="Employee full name" value={form.name} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Project / Client *</label>
              <select className={inputCls} name="project" value={form.project} onChange={set}>
                <option value="">Select project</option>
                {projects.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Designation</label>
              <input className={inputCls} name="designation" placeholder="Job title" value={form.designation} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Gender</label>
              <select className={inputCls} name="gender" value={form.gender} onChange={set}>
                <option>Male</option><option>Female</option><option>Unknown</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Nationality</label>
              <input className={inputCls} name="nationality" placeholder="e.g. Bangladeshi" value={form.nationality} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Date of Joining</label>
              <input className={inputCls} type="date" name="joiningDate" value={form.joiningDate || ''} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Date of Birth</label>
              <input className={inputCls} type="date" name="dob" value={form.dob || ''} onChange={set} />
            </div>

            <div className={sectionCls}>Compliance</div>

            <div>
              <label className={labelCls}>Iqama No.</label>
              <input className={inputCls} name="iqamaNo" placeholder="Iqama number" value={form.iqamaNo} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Iqama Expiry</label>
              <input className={inputCls} type="date" name="iqamaExpiry" value={form.iqamaExpiry || ''} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Iqama Status</label>
              <select className={inputCls} name="iqamaStatus" value={form.iqamaStatus} onChange={set}>
                <option>Renewed</option><option>Expired</option><option value="">Unknown</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Sponsorship</label>
              <select className={inputCls} name="sponsorship" value={form.sponsorship} onChange={set}>
                <option value="">Select sponsorship</option>
                {sponsorships.map(s => <option key={s}>{s}</option>)}
                {form.sponsorship && !sponsorships.includes(form.sponsorship) && <option>{form.sponsorship}</option>}
              </select>
            </div>
            <div>
              <label className={labelCls}>Qiwa Start</label>
              <input className={inputCls} type="date" name="qiwaStart" value={form.qiwaStart || ''} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Qiwa Expiry</label>
              <input className={inputCls} type="date" name="qiwaEnd" value={form.qiwaEnd || ''} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Passport No.</label>
              <input className={inputCls} name="passportNo" placeholder="Passport number" value={form.passportNo || ''} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Passport Expiry</label>
              <input className={inputCls} type="date" name="passportExpiry" value={form.passportExpiry || ''} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Health Insurance Expiry</label>
              <input className={inputCls} type="date" name="healthInsuranceExpiry" value={form.healthInsuranceExpiry || ''} onChange={set} />
            </div>

            <div className={sectionCls}>Compensation</div>

            <div>
              <label className={labelCls}>Basic Salary (SAR)</label>
              <input className={inputCls} type="number" name="basicSalary" placeholder="0" value={form.basicSalary} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Allowance (SAR)</label>
              <input className={inputCls} type="number" name="allowance" placeholder="0" value={form.allowance} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Total Salary (SAR)</label>
              <input className={`${inputCls} bg-slate-50 text-slate-500 cursor-not-allowed`} type="number" name="totalSalary" placeholder="0"
                value={(Number(form.basicSalary) || 0) + (Number(form.allowance) || 0)} readOnly tabIndex={-1} />
              <p className="text-[10px] text-slate-400 mt-1">Auto: Basic + Allowance</p>
            </div>
            <div>
              <label className={labelCls}>Outstanding Deduction (SAR)</label>
              <input className={inputCls} type="number" name="deductionBalance" placeholder="0"
                value={form.deductionBalance ?? ''} onChange={set} />
              <p className="text-[10px] text-slate-400 mt-1">Set Monthly Installment below — without it, nothing is recovered.</p>
            </div>
            <div>
              <label className={labelCls}>Monthly Installment (SAR)</label>
              <input className={inputCls} type="number" name="monthlyInstallment" placeholder="e.g. 500"
                value={form.monthlyInstallment ?? ''} onChange={set} />
              <p className="text-[10px] text-slate-400 mt-1">Per-month recovery from balance. Blank/0 = no deduction.</p>
            </div>
            <div>
              <label className={labelCls}>Contact No.</label>
              <input className={inputCls} name="contact" placeholder="05XXXXXXXX" value={form.contact} onChange={set} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>IBAN</label>
              <input className={inputCls} name="iban" placeholder="SA..." value={form.iban} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} name="status" value={form.status} onChange={set}>
                <option>Active</option>
                {form.status === 'Terminated' && <option>Terminated</option>}
              </select>
              {form.status !== 'Terminated' && (
                <p className="text-[10px] text-slate-400 mt-1">To end employment use Terminate &amp; Settle in the employee profile.</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Comments</label>
              <input className={inputCls} name="comments" placeholder="Any notes..." value={form.comments} onChange={set} />
            </div>
          </div>
        </div>

        {/* Footer */}
        {error && (
          <div className="mx-1 mb-3 px-4 py-3 rounded-xl text-[13px] bg-red-50 border border-red-200 text-red-600">{error}</div>
        )}
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100 flex-shrink-0">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.name || !form.project}
            className="px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Employee'}
          </button>
        </div>
      </div>
    </div>
  );
}